import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { AxiosError } from 'axios'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useCookies } from 'react-cookie'
import { useSearchParam } from 'react-use'
import Box from '@components/Box'
import { STypography, STypographyDefault, STypographyDesc } from '@components/Typography'
import { above } from '@styles/mixins/breakpoints'
import Validator from '@utils/validation/Validator'
import { TValidatorRules } from '@utils/validation/Validator/Validator'
import Input, { MaskedInput } from '@components/Input'
import { CForm, CFormItem } from '@components/Utils/CForm'
import Checkbox from '@components/Checkbox'
import { SButton } from '@components/Button/Button'
import SendedImage from '@images/completed.png'
import theme from '@styles/theme'
import Form from '@api/Form'

const Wrapper = styled(Box)`
  padding: 20px;
  border-radius: 12px;
  width: 100%;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.steelGray};
  background: ${({ theme }) => theme.colors.white};
  ${above('md')(css`
    padding: 64px;
    width: auto;
    min-width: 612px;
  `)}
  ${above('xl')(css`
    min-width: 780px;
  `)}
`

const Content = styled(Box)`
  width: 100%;
  margin: 0 auto;
  max-width: 380px;
`

export const SSubmitButton = styled(SButton)`
  display: block;
  width: 100%;
`

const SLink = styled.div`
  color: ${({ theme }) => theme.colors.green};
  border: none;
  background: none;
  outline: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: inline;
`
const SLinkWrapper = styled(Box)`
  font-size: 12px;
  line-height: 16px;
  max-width: 250px;
  margin-left: -10px;
  ${above('md')(css`
    margin-left: -2px;
  `)}
`

const STitle = styled(STypography)`
  font-size: 22px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.graphite};
  ${above('md')(css`
    font-size: 24px;
    line-height: 32px;
  `)}
  ${above('xl')(css`
    font-size: 24px;
    color: #2d3033;
    line-height: 28px;
  `)}
`

interface IForm {
  name: string
  company: string
  inn?: string
  phone: string
  email?: string
  confirm: boolean
}

const ValidationRules: TValidatorRules<IForm> = {
  name: new Validator().required('Введите имя').rusOnly('Имя должно состоять из кириллических символов').result,
  company: new Validator().required('Введите название компании').result,
  inn: new Validator().mask(/(^[0-9]{12}$|^[0-9]{10}$)|^$/, 'ИНН состоит из 10 или 12 цифр').result,
  phone: new Validator().required('Введите номер телефона').phone('Введите корректный номер телефона').result,
  email: new Validator().email('Введите корректный email').result,
  confirm: new Validator().required('Необходимо согласие на обработку персональных данных').result,
}

const storageKey = 'isSmallBusinessFormSend'

const SFormBlock: React.FC = () => {
  const utmCampaignUrl = useSearchParam('utm_campaign')
  const [cookies, setCookies] = useCookies(['utmCampaign'])
  const [isSended, setIsSended] = useState(false)
  const [isRevisiting, setIsRevisiting] = useState(false)

  const handleSubmit: SubmitHandler<IForm> = async ({ name, company, inn, phone, email }) => {
    const tPhone = phone.substr(4).replaceAll(/[() -]/gi, '')
    const utmCampaignCookie = cookies.utmCampaign
    const datePlus14Days = new Date()
    datePlus14Days.setDate(datePlus14Days.getDate() + 14)

    let utm_campaign = ''

    if (utmCampaignUrl && !utmCampaignCookie) utm_campaign = utmCampaignUrl
    if (!utmCampaignUrl && utmCampaignCookie) utm_campaign = utmCampaignCookie
    if (utmCampaignUrl && utmCampaignCookie) utm_campaign = `${utmCampaignUrl}, ${utmCampaignCookie}`

    if (utmCampaignUrl) {
      setCookies('utmCampaign', utmCampaignUrl, {
        path: '/',
        expires: datePlus14Days,
        sameSite: 'lax',
      })
    }

    try {
      await Form.send({ name, company, inn: inn ?? '', email: email ?? '', phone: tPhone, utm_campaign })

      setIsSended(true)

      sessionStorage.setItem(storageKey, 'true')
    } catch (error) {
      console.log(error)
    }
  }
  const useFormState = useForm<IForm>({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: { confirm: true },
  })

  const { formState } = useFormState
  const { isDirty, isValid } = formState
  const handlePush = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    window.open('https://npfsberbanka.ru/agreement/', '_blank')
  }

  const handleDocument = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    window.open('/upload/1110-ПО-2.pdf', '_blank')
  }

  useEffect(() => {
    const storageData = !!sessionStorage.getItem(storageKey)

    if (storageData) {
      setIsRevisiting(true)
    }
  }, [])

  return (
    <Wrapper>
      <Content>
        {isSended || isRevisiting ? (
          <>
            <Box display="flex" justifyContent="center">
              <img src={SendedImage} alt="Sended" />
            </Box>
            <STypography
              textAlign="center"
              fontSize="29px"
              lineHeight="33px"
              fontWeight="600"
              mb="16px"
              dangerouslySetInnerHTML={{
                __html: `Ваша заявка ${isRevisiting && !isSended ? 'уже была' : ''} отправлена в&nbsp;СберНПФ!`,
              }}
              fontFamily={theme.typography.headingFontFamily}
            />
            <STypographyDesc textAlign="center" lineHeight="20px">
              Мы свяжемся с&nbsp;вами в&nbsp;течение одного рабочего дня и&nbsp;расскажем о&nbsp;заинтересовавшей вас
              корпоративной пенсионной программе или&nbsp;подберем альтернативные варианты
            </STypographyDesc>
          </>
        ) : (
          <>
            <STitle width="100%" mb="16px" fontFamily={theme.typography.defaultFontFamily}>
              Заявка на&nbsp;подбор программы
            </STitle>
            <STypographyDefault mb="32px" color="darkGray">
              Оставьте свои контакты, и&nbsp;мы подберем индивидуальную программу, которая решит задачи вашего бизнеса.
            </STypographyDefault>
            <CForm<IForm> rules={ValidationRules} useFormState={useFormState} onSubmit={handleSubmit}>
              <CFormItem mb={14} name="name" label="Ваше имя">
                <Input placeholder="Иван Иванов" />
              </CFormItem>
              <CFormItem mb={14} name="company" label="Название компании">
                <Input placeholder="Название компании" />
              </CFormItem>
              <CFormItem mb={14} name="inn" label="ИНН">
                <Input />
              </CFormItem>
              <CFormItem
                mb={{ xs: 18, md: 9 }}
                name="phone"
                label="Телефон"
                help="Мы позвоним на&nbsp;этот номер и&nbsp;расскажем о&nbsp;нашем предложении"
              >
                <MaskedInput mask="+7 (111) 111-11-11" size="large" placeholder="+7 (9__) ___-__-__" />
              </CFormItem>
              <CFormItem
                mb={{ xs: 18, md: 9 }}
                name="email"
                label="Email"
                help="Мы вышлем предложение для детального ознакомления"
              >
                <Input placeholder="example@email.com" />
              </CFormItem>
              <CFormItem
                mb={14}
                name="confirm"
                label={
                  <SLinkWrapper>
                    <span>
                      Я{' '}
                      <SLink onClick={(e: React.MouseEvent<HTMLElement>) => handleDocument(e)}>
                        ознакомлен с политикой в области обработки и защиты персональных данных АО "НПФ Сбербанка"
                      </SLink>{' '}
                      и согласен
                      <SLink onClick={(e: React.MouseEvent<HTMLElement>) => handlePush(e)}>
                        {' '}
                        на обработку и&nbsp;передачу персональных данных
                      </SLink>
                    </span>
                  </SLinkWrapper>
                }
              >
                <Checkbox />
              </CFormItem>
              <SSubmitButton type="submit" disabled={!isDirty || !isValid}>
                Отправить заявку
              </SSubmitButton>
            </CForm>
          </>
        )}
      </Content>
    </Wrapper>
  )
}

export default SFormBlock
